import * as React from 'react';
import {
  Check,
  ChevronRight,
  ChevronsUpDown,
  ChartColumnBig,
  GitCompare,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Moon,
  Palette,
  Rocket,
  Settings,
  Settings2,
  Shield,
  SquareActivity,
  Sun,
  Upload,
  CloudUpload,
  FileUp,
  ArrowRight,
  Users,
  User,
  Workflow,
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  Switch,
  useTheme,
} from '@rnd-payplus/alloy-react';
import { useTableDensity } from '@rnd-payplus/alloy-react';

// --- Types ---

interface TCHSidebarProps {
  user?: { name: string; email: string } | null;
  onLogout?: () => void;
  profileHref?: string;
  currentPath?: string;
  renderLink?: (href: string, children: React.ReactNode) => React.ReactNode;
}

// --- Nav structure matching Figma design ---

interface NavItemDef {
  href: string;
  label: string;
  icon: React.ReactNode;
}

const standaloneItems: NavItemDef[] = [
  { href: '/onboarding-wizard', label: 'Onboarding Wizard', icon: <Rocket className="size-4" /> },
];

const mainItems: NavItemDef[] = [
  { href: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="size-4" /> },
  { href: '/tenants', label: 'Tenants', icon: <Users className="size-4" /> },
  { href: '/integrations', label: 'Integrations', icon: <GitCompare className="size-4" /> },
  { href: '/data-flows', label: 'Data Flows', icon: <Workflow className="size-4" /> },
];

const integrationToolsItems: NavItemDef[] = [
  { href: '/ingest', label: 'Ingest', icon: <FileUp className="size-4" /> },
  { href: '/sap-payments', label: 'SAP Payments', icon: <Upload className="size-4" /> },
  { href: '/invoice-ocr', label: 'Invoice OCR', icon: <Upload className="size-4" /> },
  { href: '/storage-connections', label: 'Storage Connections', icon: <CloudUpload className="size-4" /> },
  { href: '/data-mappings', label: 'Mappings', icon: <ArrowRight className="size-4" /> },
];

const monitoringItems: NavItemDef[] = [
  { href: '/analytics', label: 'Analytics', icon: <ChartColumnBig className="size-4" /> },
  { href: '/services-health', label: 'Services Health', icon: <SquareActivity className="size-4" /> },
  { href: '/observability', label: 'Observability', icon: <Settings2 className="size-4" /> },
];

const adminItems: NavItemDef[] = [
  { href: '/configuration', label: 'Configuration', icon: <Settings className="size-4" /> },
  { href: '/security', label: 'Security', icon: <Shield className="size-4" /> },
];

const footerNavItems: NavItemDef[] = [
  { href: '/help', label: 'Help & feedback', icon: <LifeBuoy className="size-4" /> },
  { href: '/settings', label: 'Settings', icon: <Settings2 className="size-4" /> },
];

// --- Helpers ---

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

function isNavActive(currentPath: string, href: string): boolean {
  return (
    currentPath === href ||
    currentPath.startsWith(href + '?') ||
    currentPath.startsWith(href + '/')
  );
}

function defaultRenderLink(href: string, children: React.ReactNode): React.ReactNode {
  return <a href={href}>{children}</a>;
}

// --- Component ---

export function TCHSidebar({
  user,
  onLogout,
  profileHref = '/profile',
  currentPath: currentPathProp,
  renderLink = defaultRenderLink,
}: TCHSidebarProps) {
  const currentPath = currentPathProp ?? '';
  const { theme, setTheme } = useTheme();
  const { density, setDensity } = useTableDensity();
  const [isDarkMode, setIsDarkMode] = React.useState(false);

  React.useEffect(() => {
    const root = window.document.documentElement;
    setIsDarkMode(root.classList.contains('dark'));
    const observer = new MutationObserver(() => setIsDarkMode(root.classList.contains('dark')));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [theme]);

  const toggleDarkMode = (checked: boolean) => setTheme(checked ? 'dark' : 'light');

  const renderNavItem = (item: NavItemDef) => (
    <SidebarMenuItem key={item.href}>
      <SidebarMenuButton asChild isActive={isNavActive(currentPath, item.href)}>
        {renderLink(
          item.href,
          <>
            {item.icon}
            <span>{item.label}</span>
          </>,
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const renderGroup = (label: string, items: NavItemDef[]) => (
    <SidebarGroup key={label}>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarMenu>{items.map(renderNavItem)}</SidebarMenu>
    </SidebarGroup>
  );

  return (
    <>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton className="cursor-default hover:bg-transparent active:bg-transparent">
              <span className="text-sm font-normal">Tungsten Connectivity Hub</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Onboarding Wizard — standalone above first separator */}
        <SidebarGroup>
          <SidebarMenu>{standaloneItems.map(renderNavItem)}</SidebarMenu>
        </SidebarGroup>

        <SidebarSeparator />

        {renderGroup('Main', mainItems)}

        <SidebarSeparator />

        {renderGroup('Integration Tools', integrationToolsItems)}

        <SidebarSeparator />

        {renderGroup('Monitoring', monitoringItems)}
        {renderGroup('Admin', adminItems)}
      </SidebarContent>

      <SidebarFooter>
        {/* Footer nav links */}
        <SidebarMenu>
          {footerNavItems.map(renderNavItem)}
        </SidebarMenu>

        {/* User menu */}
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  <Avatar className="size-8 rounded-lg bg-sidebar-primary">
                    <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      {user ? getInitials(user.name) : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate text-sm font-semibold leading-tight">
                      {user?.name ?? 'Guest'}
                    </span>
                    <span className="truncate text-xs font-normal leading-4 tracking-[0.12px]">
                      {user?.email ?? ''}
                    </span>
                  </div>
                  <ChevronsUpDown className="size-4 shrink-0" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="min-w-56 rounded-lg"
                align="end"
                side="top"
                sideOffset={4}
              >
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  {renderLink(
                    profileHref,
                    <>
                      <User className="size-4" />
                      <span>Profile</span>
                    </>,
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Settings2 className="size-4" />
                    <span>Settings</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Palette className="size-4" />
                        <span>Appearance</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem
                          className="flex items-center justify-between gap-2"
                          onSelect={(e) => e.preventDefault()}
                        >
                          <div className="flex items-center gap-2">
                            {isDarkMode ? <Moon className="size-4" /> : <Sun className="size-4" />}
                            <span>Dark Mode</span>
                          </div>
                          <Switch checked={isDarkMode} onCheckedChange={toggleDarkMode} />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="flex items-center justify-between gap-2"
                          onSelect={(e) => e.preventDefault()}
                        >
                          <div className="flex items-center gap-2">
                            <span>Table Density</span>
                          </div>
                          <Select
                            value={density}
                            onValueChange={(value) =>
                              setDensity(value as 'comfortable' | 'compact')
                            }
                          >
                            <SelectTrigger className="h-7 w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="comfortable">Comfortable</SelectItem>
                              <SelectItem value="compact">Compact</SelectItem>
                            </SelectContent>
                          </Select>
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout}>
                  <LogOut className="size-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </>
  );
}
